/**
 * TEMPORARY DIAGNOSTIC, Director only. Remove this file, its one mount in
 * GlobalChrome.tsx, and onResyncCheck in lib/viewportResync.ts once the
 * cause of the 2026-10-03 floating buttons report is confirmed on
 * Bobbie's phone.
 *
 * Why it exists: the floating buttons broke on her iPhone five times, and
 * every blind fix trusted numbers the device reported. The record's own
 * rule for a return of the bug (docs/lessons.md, "A repeat UI bug on her
 * device") is a temporary readout of the real viewport values, not another
 * guess. So this shows what the phone reports, small, inside the floating
 * layer (so it is on screen wherever that layer ended up), for a Director
 * to screenshot and send to Bobbie.
 *
 * It only ever READS and DISPLAYS. Nothing here writes a position or
 * touches the lift. The scale is always among the numbers it shows.
 *
 * When it opens by itself: READOUT_CHECK_MS after each re-sync check, if
 * visualViewport.offsetTop is above READOUT_MIN_OFFSET_PX, the reading
 * passes the same plausibility gate the lift uses (so the scale is within
 * SCALE_TOLERANCE of 1), and no text entry field has focus. NEVER by itself
 * on a zoomed page: iOS zooms in when a field under 16px is tapped and
 * stays zoomed, and panning a zoomed page reads as an offset, so a zoom
 * rule would pop this up after ordinary typing. While it is up, viewport
 * and window scroll events refresh the numbers, and it goes away by itself
 * when offsetTop is back to 0 or the page zooms, the moment a text field
 * takes focus, or when Hide is tapped.
 *
 * On request: a Director who loads the Hub with ?screencheck=1 in the
 * address sees it whatever the numbers, zoomed and lined up included,
 * until Hide is tapped or the page is reloaded without it. It still steps
 * aside while a text field has focus (so it never sits over the field
 * above the keyboard) and comes back when the field is left. Still
 * Director only: GlobalChrome mounts this for nobody else.
 *
 * Why it may not open by itself even with the buttons out of place: the
 * device reports offsetTop 0 and scale 1 while the frame is shifted; or no
 * check ran (no keyboard was involved); or a field still had focus; or a
 * Director is previewing another role; or the floating buttons are hidden
 * on that screen. When in doubt, open it on request with ?screencheck=1.
 *
 * Not a feature, so no How-To guide entry.
 */
import { useEffect, useRef, useState } from 'react';
import { isPlausibleViewportReading } from '../lib/fabViewportTracking';
import {
  isTextEntryElement,
  NUDGE_MIN_OFFSET_PX,
  onResyncCheck,
  textEntryHasFocus,
  type NudgeSkipReason,
  type ResyncCheck,
  type ResyncTrigger,
} from '../lib/viewportResync';

/** How long after a re-sync check the readout looks again. */
export const READOUT_CHECK_MS = 600;
/** offsetTop above this, after the re-sync had its chance, opens the readout. */
export const READOUT_MIN_OFFSET_PX = 40;
/** ?screencheck=1 opens the readout on demand (Director only, see GlobalChrome). */
export const SCREEN_CHECK_PARAM = 'screencheck';

function urlAsksForScreenCheck(): boolean {
  try {
    return new URLSearchParams(window.location.search).get(SCREEN_CHECK_PARAM) === '1';
  } catch {
    return false;
  }
}

// Read once when the app loads, before any route can rewrite the address,
// so an on-demand check stays up while a Director moves between pages.
const askedAtLoad = typeof window !== 'undefined' && urlAsksForScreenCheck();
// Hide ends an on-demand check for the rest of this page load.
let onDemandDismissed = false;

function onDemandNow(): boolean {
  return !onDemandDismissed && (askedAtLoad || urlAsksForScreenCheck());
}

interface Snapshot {
  innerHeight: number;
  height: number;
  offsetTop: number;
  pageTop: number;
  scale: number;
  scrollY: number;
  scrollHeight: number;
  clientHeight: number;
  navigatorStandalone: boolean;
  displayModeStandalone: boolean;
  lastNudge: { at: number; trigger: ResyncTrigger } | null;
  lastCheck: ResyncCheck | null;
  onDemand: boolean;
  at: number;
}

type Gate = 'show' | 'keep' | 'hide';

/**
 * When it opens by itself (not on request). The plausibility gate keeps
 * it shut on a zoomed page: see the file comment.
 */
function readoutGate(vv: VisualViewport): Gate {
  if (!isPlausibleViewportReading(window.innerHeight, vv.height, vv.offsetTop, vv.scale)) return 'hide';
  if (textEntryHasFocus()) return 'hide';
  if (vv.offsetTop > READOUT_MIN_OFFSET_PX) return 'show';
  if (vv.offsetTop > NUDGE_MIN_OFFSET_PX) return 'keep';
  return 'hide';
}

function displayModeStandalone(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  } catch {
    return false;
  }
}

function takeSnapshot(
  vv: VisualViewport,
  lastNudge: Snapshot['lastNudge'],
  lastCheck: ResyncCheck | null,
  onDemand: boolean
): Snapshot {
  const doc = document.documentElement;
  return {
    innerHeight: window.innerHeight,
    height: vv.height,
    offsetTop: vv.offsetTop,
    pageTop: vv.pageTop,
    scale: vv.scale,
    scrollY: window.scrollY,
    scrollHeight: doc.scrollHeight,
    clientHeight: doc.clientHeight,
    navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone === true,
    displayModeStandalone: displayModeStandalone(),
    lastNudge,
    lastCheck,
    onDemand,
    at: Date.now(),
  };
}

function num(value: number): string {
  return Number.isFinite(value) ? String(Math.round(value * 10) / 10) : String(value);
}

function studioTime(ms: number): string {
  return `${new Date(ms).toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })} Central`;
}

const TRIGGER_WORDS: Record<ResyncTrigger, string> = {
  focusout: 'after leaving a text field',
  'keyboard-closed': 'after the keyboard closed',
};

const SKIP_WORDS: Record<NudgeSkipReason, string> = {
  zoomed: 'the page was zoomed',
  implausible: 'the numbers looked wrong',
  typing: 'a text field had focus',
  'no-offset': 'the screen looked lined up',
  'keyboard-open': 'the keyboard looked open',
  moving: 'the page was still scrolling',
};

function yesNo(value: boolean): string {
  return value ? 'yes' : 'no';
}

export default function ScreenCheckReadout() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let timer: number | undefined;
    let lastNudge: Snapshot['lastNudge'] = null;
    let lastCheck: ResyncCheck | null = null;

    const hide = () => {
      if (!visibleRef.current) return;
      visibleRef.current = false;
      setSnapshot(null);
    };

    // On request, it is up whenever no text field has focus. Otherwise only
    // the check after a re-sync can OPEN the readout; viewport events can
    // only refresh or close one that is already up.
    const look = (mayOpen: boolean) => {
      if (onDemandNow()) {
        if (textEntryHasFocus()) {
          hide();
          return;
        }
        visibleRef.current = true;
        setSnapshot(takeSnapshot(vv, lastNudge, lastCheck, true));
        return;
      }
      const gate = readoutGate(vv);
      if (gate === 'hide') {
        hide();
        return;
      }
      if (gate === 'show' && mayOpen) visibleRef.current = true;
      if (visibleRef.current) setSnapshot(takeSnapshot(vv, lastNudge, lastCheck, false));
    };

    const offCheck = onResyncCheck((check) => {
      lastCheck = check;
      if (check.nudged) lastNudge = { at: check.at, trigger: check.trigger };
      if (timer !== undefined) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = undefined;
        look(true);
      }, READOUT_CHECK_MS);
    });
    const onViewport = () => {
      if (visibleRef.current) look(false);
    };
    // A text field taking focus always hides it, on request included, so
    // it never sits over the field above the keyboard.
    const onFocusIn = (event: FocusEvent) => {
      if (isTextEntryElement(event.target)) hide();
    };
    // On request, it comes back as soon as the field is left. A turn later,
    // so focus moving straight to another field keeps it hidden.
    let refocusTimer: number | undefined;
    const onFocusOut = (event: FocusEvent) => {
      if (!onDemandNow() || !isTextEntryElement(event.target)) return;
      if (refocusTimer !== undefined) window.clearTimeout(refocusTimer);
      refocusTimer = window.setTimeout(() => {
        refocusTimer = undefined;
        look(true);
      }, 0);
    };

    vv.addEventListener('resize', onViewport);
    vv.addEventListener('scroll', onViewport);
    // Read only, passive: keeps scrollY current while it is up.
    window.addEventListener('scroll', onViewport, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    if (onDemandNow()) look(true);
    return () => {
      offCheck();
      vv.removeEventListener('resize', onViewport);
      vv.removeEventListener('scroll', onViewport);
      window.removeEventListener('scroll', onViewport);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (timer !== undefined) window.clearTimeout(timer);
      if (refocusTimer !== undefined) window.clearTimeout(refocusTimer);
      visibleRef.current = false;
    };
  }, []);

  if (!snapshot) return null;

  const nudgeLine = snapshot.lastNudge
    ? `Nudge: ran at ${studioTime(snapshot.lastNudge.at)}, ${TRIGGER_WORDS[snapshot.lastNudge.trigger]}`
    : 'Nudge: has not run';
  const lastCheckLine = snapshot.lastCheck
    ? `Last check: ${
        snapshot.lastCheck.skipped ? `skipped, ${SKIP_WORDS[snapshot.lastCheck.skipped]}` : 'nudged'
      } at ${studioTime(snapshot.lastCheck.at)}`
    : null;

  return (
    <div
      role="status"
      data-screen-check=""
      style={{
        position: 'absolute',
        left: 'calc(env(safe-area-inset-left, 0px) + 14px)',
        right: 'calc(env(safe-area-inset-right, 0px) + 14px)',
        // Above the whole floating band (the Assistant bubble stacked over
        // jump-to-top), the same 140px GlobalChrome reserves for it as
        // FAB_CLEARANCE_H, so it never covers a floating control.
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 140px)',
        maxWidth: 360,
        marginInline: 'auto',
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--line)',
        background: 'var(--paper)',
        color: 'var(--ink)',
        boxShadow: 'var(--shadow-lg)',
        fontSize: 13,
        lineHeight: 1.45,
        fontVariantNumeric: 'tabular-nums',
        overflowWrap: 'anywhere',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <strong>Screen check</strong>
        <button
          type="button"
          onClick={() => {
            onDemandDismissed = true;
            visibleRef.current = false;
            setSnapshot(null);
          }}
          style={{
            minHeight: 44,
            minWidth: 44,
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-md)',
            background: 'transparent',
            color: 'var(--ink)',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Hide
        </button>
      </div>
      <div style={{ color: 'var(--ink-soft)', marginBottom: 4 }}>Please screenshot this and send it to Bobbie.</div>
      <div>
        {snapshot.onDemand ? 'Opened on request (screencheck=1)' : 'Opened by itself after a check'}
      </div>
      <div>innerHeight {num(snapshot.innerHeight)}</div>
      <div>
        visualViewport height {num(snapshot.height)}, offsetTop {num(snapshot.offsetTop)}, pageTop{' '}
        {num(snapshot.pageTop)}, scale {num(snapshot.scale)}
      </div>
      <div>scrollY {num(snapshot.scrollY)}</div>
      <div>
        scrollHeight {num(snapshot.scrollHeight)}, clientHeight {num(snapshot.clientHeight)}
      </div>
      <div>
        Home screen app: {yesNo(snapshot.navigatorStandalone || snapshot.displayModeStandalone)} (standalone{' '}
        {yesNo(snapshot.navigatorStandalone)}, display-mode {yesNo(snapshot.displayModeStandalone)})
      </div>
      <div>{nudgeLine}</div>
      {lastCheckLine && <div>{lastCheckLine}</div>}
      <div>Read at {studioTime(snapshot.at)}</div>
    </div>
  );
}
